/**
 * Box 7: the three loudest /insights events over /insights' own 30-day window
 * (ruling 6), not the page's period, so the caption names the window.
 *
 * /insights is hidden since 2026-10-16 (owner): its items are plain text, since
 * a link there would only land on the start page. The sync items keep their
 * link to /settings/integrations.
 *
 * @module components/looker/app/AttentionBox
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isHiddenPage } from '@/services/hiddenPages';
import type { AttentionItem } from '@/services/looker/lookerAppData';
import { BoxMessage } from './appBoxShared';

const MAX_ITEMS = 3;

const SEVERITY_DOT: Record<AttentionItem['severity'], string> = {
  1: 'bg-error-700',
  2: 'bg-warning-700',
  3: 'bg-neutral-700',
  4: 'bg-neutral-700',
};

interface Props {
  events: AttentionItem[];
  windowDays: number;
  truncated: boolean;
}

export const AttentionBox: React.FC<Props> = ({ events, windowDays, truncated }) => {
  const { t } = useTranslation();
  return (
    <div>
      <p className="text-xs font-medium text-neutral-700">{t('looker.app.attention.window', { days: windowDays })}</p>
      {truncated && (
        <p role="alert" className="mt-2 rounded-md border border-warning-300 bg-warning-50 px-3 py-2 text-xs text-warning-700">
          {t('looker.app.attention.truncated')}
        </p>
      )}
      {events.length === 0 ? (
        <BoxMessage text={t('looker.app.attention.none')} />
      ) : (
        <ul className="mt-2 divide-y divide-neutral-100 text-sm">
          {events.slice(0, MAX_ITEMS).map((e, i) => {
            const row = (
              <>
                <span aria-hidden="true" className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEVERITY_DOT[e.severity]}`} />
                <span className="min-w-0 break-words">{e.text}</span>
              </>
            );
            return (
              <li key={`${e.link}|${e.text}|${i}`} className="py-2.5">
                {isHiddenPage(e.link) ? (
                  <div className="flex items-start gap-2 text-neutral-900">{row}</div>
                ) : (
                  <Link to={e.link} className="flex items-start gap-2 text-neutral-900 underline-offset-2 hover:underline">
                    {row}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
